"use client";

import { useState } from "react";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Loader2, Camera } from "lucide-react";
import { captureIssuePhoto, isNativePlatform } from "@/lib/issue-photo";
import { PhotoCaptureSheet } from "@/components/ui/photo-capture-sheet";

interface IssuePhotoFabProps {
  blockId: Id<"blocks">;
  className?: string;
  onPhotoAdded?: () => void;
}

export function IssuePhotoFab({
  blockId,
  className,
  onPhotoAdded,
}: IssuePhotoFabProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedPath, setCapturedPath] = useState<string | null>(null);
  const [showCaptureSheet, setShowCaptureSheet] = useState(false);

  if (!isNativePlatform()) {
    return null;
  }

  const handleCapture = async () => {
    setIsCapturing(true);
    try {
      const photoPath = await captureIssuePhoto();
      if (photoPath) {
        setCapturedPath(photoPath);
        setShowCaptureSheet(true);
      }
    } catch (error) {
      console.error("Failed to capture issue photo:", error);
      alert("Failed to capture photo");
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className={className}
        onClick={handleCapture}
        disabled={isCapturing}
      >
        {isCapturing ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Camera className="h-5 w-5" />
        )}
        <span className="sr-only">Add issue photo</span>
      </Button>

      {capturedPath && (
        <PhotoCaptureSheet
          blockId={blockId}
          photoPath={capturedPath}
          open={showCaptureSheet}
          onOpenChange={setShowCaptureSheet}
          onSaved={() => {
            onPhotoAdded?.();
            setCapturedPath(null);
          }}
          onCancelled={() => setCapturedPath(null)}
        />
      )}
    </>
  );
}
